import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AppIcon } from '../../../../components/icons/AppIcon';
import { useCloseOnScroll } from '../../../../hooks/useCloseOnScroll';

interface MessageContextMenuProps {
    x: number;
    y: number;
    onReply: () => void;
    onEdit?: () => void;
    onDelete?: () => void;
    onClose: () => void;
    canEdit?: boolean;
    canDelete?: boolean;
    boundaryRef?: React.RefObject<HTMLElement | null>;
    preferLeft?: boolean;
}

const PADDING = 8;
const MENU_MIN_WIDTH = 168;
const GESTURE_END_ARM_MS = 400;

let activeTouches = 0;
let touchTrackerReady = false;

/** Sabe se o dedo ainda está na tela quando o menu abre (o listener do menu chega tarde). */
function bindTouchTracker(): void {
    if (touchTrackerReady || typeof document === 'undefined') return;
    touchTrackerReady = true;
    document.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse') activeTouches += 1;
    }, true);
    const release = (event: PointerEvent) => {
        if (event.pointerType === 'mouse') return;
        activeTouches = Math.max(0, activeTouches - 1);
    };
    document.addEventListener('pointerup', release, true);
    document.addEventListener('pointercancel', release, true);
}

bindTouchTracker();

type Bounds = Pick<DOMRect, 'left' | 'top' | 'right' | 'bottom'>;

type MenuPosition = { left: number; top: number; transform?: string };

function resolveBounds(boundary?: HTMLElement | null): Bounds {
    const viewport: Bounds = {
        left: PADDING,
        top: PADDING,
        right: window.innerWidth - PADDING,
        bottom: window.innerHeight - PADDING,
    };

    if (!boundary) return viewport;

    const rect = boundary.getBoundingClientRect();
    return {
        left: Math.max(viewport.left, rect.left + PADDING),
        top: Math.max(viewport.top, rect.top + PADDING),
        right: Math.min(viewport.right, rect.right - PADDING),
        bottom: Math.min(viewport.bottom, rect.bottom - PADDING),
    };
}

function clampMenuPosition(
    x: number,
    y: number,
    menuWidth: number,
    menuHeight: number,
    bounds: Bounds,
    preferLeft: boolean,
): MenuPosition {
    let transform = preferLeft ? 'translateX(-100%)' : undefined;
    let left = x;
    let top = y;

    const fits = () => {
        const effectiveLeft = transform ? left - menuWidth : left;
        return (
            effectiveLeft >= bounds.left &&
            left <= bounds.right &&
            top >= bounds.top &&
            top + menuHeight <= bounds.bottom
        );
    };

    if (preferLeft && !fits()) {
        transform = undefined;
        left = Math.min(x, bounds.right - menuWidth);
    }

    if (!preferLeft && left + menuWidth > bounds.right) {
        transform = 'translateX(-100%)';
        left = x;
    }

    const effectiveLeft = transform ? left - menuWidth : left;
    left = Math.max(bounds.left + (transform ? menuWidth : 0), Math.min(left, bounds.right));
    if (effectiveLeft < bounds.left) {
        transform = undefined;
        left = Math.max(bounds.left, Math.min(x, bounds.right - menuWidth));
    }

    top = Math.max(bounds.top, Math.min(top, bounds.bottom - menuHeight));

    return { left, top, transform };
}

export const MessageContextMenu: React.FC<MessageContextMenuProps> = ({
    x,
    y,
    onReply,
    onEdit,
    onDelete,
    onClose,
    canEdit = false,
    canDelete = false,
    boundaryRef,
    preferLeft = false,
}) => {
    const menuRef = useRef<HTMLDivElement>(null);
    const [position, setPosition] = useState<MenuPosition | null>(null);
    const [armed, setArmed] = useState(false);

    useCloseOnScroll(onClose, armed);

    useLayoutEffect(() => {
        const menu = menuRef.current;
        if (!menu) return;

        const width = Math.max(menu.offsetWidth, MENU_MIN_WIDTH);
        const height = Math.max(menu.offsetHeight, menu.scrollHeight);
        const bounds = resolveBounds(boundaryRef?.current);
        setPosition(clampMenuPosition(x, y, width, height, bounds, preferLeft));
    }, [x, y, canEdit, canDelete, boundaryRef, preferLeft]);

    useEffect(() => {
        bindTouchTracker();
        const coarse = window.matchMedia('(pointer: coarse)').matches;
        if (!coarse) {
            setArmed(true);
            return;
        }

        let armTimer: ReturnType<typeof setTimeout> | null = null;
        const armIfReleased = () => {
            if (armTimer) clearTimeout(armTimer);
            armTimer = setTimeout(() => {
                if (activeTouches === 0) setArmed(true);
            }, GESTURE_END_ARM_MS);
        };

        if (activeTouches === 0) armIfReleased();
        document.addEventListener('pointerup', armIfReleased, true);
        document.addEventListener('pointercancel', armIfReleased, true);

        return () => {
            if (armTimer) clearTimeout(armTimer);
            document.removeEventListener('pointerup', armIfReleased, true);
            document.removeEventListener('pointercancel', armIfReleased, true);
        };
    }, []);

    useEffect(() => {
        if (!armed) return;

        const handlePointerDown = (e: MouseEvent | TouchEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
                onClose();
            }
        };

        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };

        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('touchstart', handlePointerDown);
        document.addEventListener('keydown', handleEscape);

        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('touchstart', handlePointerDown);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [onClose, armed]);

    const menuItemClass =
        'flex min-h-11 w-full items-center gap-s2 px-s4 py-s2 text-left text-base text-secondary transition-colors hover:bg-surface-subtle focus-visible:bg-surface-subtle focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring';

    const menu = (
        <div
            ref={menuRef}
            className="fixed z-tooltip min-w-[10.5rem] rounded-lg border border-border bg-card py-s1 shadow-md"
            style={{
                left: position?.left ?? x,
                top: position?.top ?? y,
                transform: position?.transform,
                opacity: position ? 1 : 0,
                pointerEvents: position && armed ? 'auto' : 'none',
            }}
            role="menu"
            aria-label="Opções da mensagem"
        >
            <button
                type="button"
                onClick={() => {
                    onReply();
                    onClose();
                }}
                className={menuItemClass}
                role="menuitem"
            >
                <AppIcon name="corner-up-left" className="size-4 shrink-0" aria-hidden="true" />
                Responder
            </button>
            {canEdit && onEdit && (
                <button
                    type="button"
                    onClick={() => {
                        onEdit();
                        onClose();
                    }}
                    className={menuItemClass}
                    role="menuitem"
                >
                    <AppIcon name="edit" className="size-4 shrink-0" aria-hidden="true" />
                    Editar
                </button>
            )}
            {canDelete && onDelete && (
                <button
                    type="button"
                    onClick={() => {
                        onDelete();
                        onClose();
                    }}
                    className={`${menuItemClass} border-t border-border text-danger-text hover:bg-danger-bg focus-visible:bg-danger-bg`}
                    role="menuitem"
                >
                    <AppIcon name="trash" className="size-4 shrink-0" aria-hidden="true" />
                    Excluir
                </button>
            )}
        </div>
    );

    if (typeof document === 'undefined') return menu;

    return createPortal(menu, document.body);
};
